/**
 * The end-to-end pipeline: parse → plan sources → over-generate candidate
 * queries → execute → rank by non-empty → normalize the answer. Over-generation
 * here means retrying with the runner-up *property* links when the primary query
 * comes back empty (the QAnswer "generate many, keep what returns results" idea).
 */
import { iri } from "../ir/build.ts";
import type { QueryIR } from "../ir/types.ts";
import type { SparqlBackend } from "../backends/types.ts";
import type { ExecuteOptions, QueryExecutor, SparqlResults } from "../execute/types.ts";
import type { ParseContext, ParseResult, ParseTrace, SemanticParser } from "../parser/types.ts";
import { ParseError } from "../parser/types.ts";
import type { FederationPlanner, SourcePlan } from "../sources/federation-planner.ts";
import type { Source, SourceRegistry } from "../sources/types.ts";

export interface AskDeps {
  parser: SemanticParser;
  registry: SourceRegistry;
  planner: FederationPlanner;
  /** Resolve the SPARQL backend for a source (Wikidata dialect vs. generic). */
  backendFor: (source: Source) => SparqlBackend | undefined;
  /** Route execution per source (remote fetch vs. local-store evaluator). */
  executorFor: (source: Source) => QueryExecutor;
  /** How many runner-up property links to try when the primary is empty. */
  maxAlternatives?: number;
}

export interface Attempt {
  strategy: string;
  description: string;
  sparql: string;
  sourceId: string;
  rowCount: number;
  empty: boolean;
  elapsedMs?: number;
  error?: string;
}

export interface AnswerCell {
  var: string;
  display: string;
  iri?: string;
}

export type AnswerView =
  | { kind: "count"; value: number }
  | { kind: "boolean"; value: boolean }
  | { kind: "table"; columns: string[]; rows: AnswerCell[][] };

export interface AskResult {
  question: string;
  ok: boolean;
  error?: string;
  trace?: ParseTrace;
  plan?: SourcePlan;
  sourceId?: string;
  attempts: Attempt[];
  chosenIndex?: number;
  answer?: AnswerView;
}

/** Clone an IR, swapping every occurrence of one predicate IRI for another. */
function swapPredicate(ir: QueryIR, fromIri: string, toIri: string, toLabel: string): QueryIR {
  return {
    ...ir,
    patterns: ir.patterns.map((p) =>
      p.predicate.kind === "iri" && p.predicate.value === fromIri
        ? { ...p, predicate: iri(toIri, toLabel) }
        : p,
    ),
  };
}

/** Clone an IR, swapping every subject/object occurrence of one IRI for another. */
function swapEntity(ir: QueryIR, fromIri: string, toIri: string, toLabel: string): QueryIR {
  const repl = (t: QueryIR["patterns"][number]["subject"]) =>
    t.kind === "iri" && t.value === fromIri ? iri(toIri, toLabel) : t;
  return {
    ...ir,
    patterns: ir.patterns.map((p) => ({ ...p, subject: repl(p.subject), object: repl(p.object) })),
  };
}

interface Variant {
  ir: QueryIR;
  strategy: string;
  description: string;
}

/** Primary IR plus IRs that swap in runner-up property candidates. */
function buildVariants(parse: ParseResult, maxAlternatives: number): Variant[] {
  const variants: Variant[] = [
    { ir: parse.ir, strategy: "primary", description: "Best entity + property links." },
  ];
  const relation = parse.trace.relations[0];
  if (relation?.chosen) {
    const alts = relation.alternatives.filter((c) => c.iri !== relation.chosen!.iri).slice(0, maxAlternatives);
    for (const alt of alts) {
      variants.push({
        ir: swapPredicate(parse.ir, relation.chosen.iri, alt.iri, alt.label),
        strategy: `alt-property:${alt.id ?? alt.label}`,
        description: `Retry with property "${alt.label}" (${alt.id ?? "?"}) instead.`,
      });
    }
  }

  const entity = parse.trace.entities[0];
  if (entity?.chosen) {
    const alts = entity.alternatives.filter((c) => c.iri !== entity.chosen!.iri).slice(0, maxAlternatives);
    for (const alt of alts) {
      variants.push({
        ir: swapEntity(parse.ir, entity.chosen.iri, alt.iri, alt.label),
        strategy: `alt-entity:${alt.id ?? alt.label}`,
        description: `Retry with entity "${alt.label}" (${alt.id ?? "?"}) instead.`,
      });
    }
  }
  return variants;
}

function isNonEmpty(ir: QueryIR, results: SparqlResults): boolean {
  if (ir.form === "count") return Number(results.rows[0]?.["count"]?.value ?? "0") > 0;
  if (typeof results.boolean === "boolean") return true;
  return results.rows.length > 0;
}

function buildAnswer(ir: QueryIR, results: SparqlResults): AnswerView {
  if (ir.form === "count") {
    return { kind: "count", value: Number(results.rows[0]?.["count"]?.value ?? "0") };
  }
  if (typeof results.boolean === "boolean") {
    return { kind: "boolean", value: results.boolean };
  }
  const columns = ir.projection;
  const rows = results.rows.map((row) =>
    columns.map((varName): AnswerCell => {
      const labelBinding = row[`${varName}Label`];
      const rawBinding = row[varName];
      const cell: AnswerCell = {
        var: varName,
        display: labelBinding?.value ?? rawBinding?.value ?? "",
      };
      if (rawBinding?.type === "uri") cell.iri = rawBinding.value;
      return cell;
    }),
  );
  return { kind: "table", columns, rows };
}

export async function ask(
  question: string,
  deps: AskDeps,
  ctx?: ParseContext,
): Promise<AskResult> {
  const maxAlternatives = deps.maxAlternatives ?? 2;

  // 1. Parse (NL → grounded IR + trace).
  let parse: ParseResult;
  try {
    parse = await deps.parser.parse(question, ctx);
  } catch (err) {
    if (err instanceof ParseError) {
      const partial = err.trace;
      return {
        question,
        ok: false,
        error: err.message,
        ...(isFullTrace(partial) ? { trace: partial } : {}),
        attempts: [],
      };
    }
    throw err;
  }

  // 2. Plan which source(s) answer it.
  const plan = deps.planner.plan(parse.ir, deps.registry);
  const source = deps.registry.get(plan.primarySourceId) ?? deps.registry.defaultSource();
  const backend = deps.backendFor(source);
  if (!backend) {
    return {
      question,
      ok: false,
      error: `No backend registered for source "${source.id}" (backendId "${source.backendId}").`,
      trace: parse.trace,
      plan,
      sourceId: source.id,
      attempts: [],
    };
  }

  // 3. Over-generate + execute + rank.
  const executor = deps.executorFor(source);
  const variants = buildVariants(parse, maxAlternatives);
  const attempts: Attempt[] = [];
  const attemptIrs: QueryIR[] = [];
  let chosenIndex: number | undefined;

  outer: for (const variant of variants) {
    for (const candidate of backend.emit(variant.ir, source)) {
      const strategy =
        variant.strategy === "primary" ? candidate.strategy : `${variant.strategy}/${candidate.strategy}`;
      const attempt: Attempt = {
        strategy,
        description: variant.strategy === "primary" ? candidate.description : variant.description,
        sparql: candidate.sparql,
        sourceId: source.id,
        rowCount: 0,
        empty: true,
      };
      try {
        const execOpts: ExecuteOptions = { ir: variant.ir };
        if (ctx?.signal) execOpts.signal = ctx.signal;
        const results = await executor.run(candidate.sparql, source, execOpts);
        attempt.elapsedMs = results.elapsedMs;
        attempt.rowCount =
          variant.ir.form === "count"
            ? Number(results.rows[0]?.["count"]?.value ?? "0")
            : results.rows.length;
        attempt.empty = !isNonEmpty(variant.ir, results);
        attempts.push(attempt);
        attemptIrs.push(variant.ir);
        if (!attempt.empty) {
          chosenIndex = attempts.length - 1;
          // Attach the winning answer via the loop's post-processing below.
          (attempt as Attempt & { _results?: SparqlResults })._results = results;
          break outer;
        }
        (attempt as Attempt & { _results?: SparqlResults })._results = results;
      } catch (err) {
        attempt.error = err instanceof Error ? err.message : String(err);
        attempts.push(attempt);
        attemptIrs.push(variant.ir);
      }
    }
  }

  // 4. Choose: first non-empty, else first successful, else nothing.
  if (chosenIndex === undefined) {
    chosenIndex = attempts.findIndex((a) => !a.error);
    if (chosenIndex < 0) chosenIndex = undefined;
  }

  let answer: AnswerView | undefined;
  if (chosenIndex !== undefined) {
    const chosen = attempts[chosenIndex] as Attempt & { _results?: SparqlResults };
    const chosenIr = attemptIrs[chosenIndex];
    if (chosen._results && chosenIr) answer = buildAnswer(chosenIr, chosen._results);
  }

  // Strip the internal results blobs before returning.
  for (const a of attempts) delete (a as Attempt & { _results?: SparqlResults })._results;

  return {
    question,
    ok: chosenIndex !== undefined && attempts[chosenIndex]?.error === undefined,
    trace: parse.trace,
    plan,
    sourceId: source.id,
    attempts,
    ...(chosenIndex !== undefined ? { chosenIndex } : {}),
    ...(answer ? { answer } : {}),
    ...(attempts.every((a) => a.error) && attempts.length
      ? { error: attempts[0]?.error }
      : {}),
  };
}

function isFullTrace(t: Partial<ParseTrace>): t is ParseTrace {
  return (
    typeof t.question === "string" &&
    typeof t.template === "string" &&
    Array.isArray(t.entities) &&
    Array.isArray(t.relations)
  );
}
